import { create } from 'zustand';
import { SnackbarMessage, SnackbarSeverity } from '../components/Snackbar/types';

interface SnackbarState {
  snackbars: SnackbarMessage[];
  addSnackbar: (
    message: string,
    severity: SnackbarSeverity,
    errorDetails?: string,
    showContactSupport?: boolean,
    duration?: number
  ) => string;
  removeSnackbar: (id: string) => void;
  clearAll: () => void;
}

export const useSnackbarStore = create<SnackbarState>((set) => ({
  snackbars: [],

  addSnackbar: (message, severity, errorDetails, showContactSupport, duration = 5000) => {
    const id = `${Date.now()}-${Math.random()}`;
    set((state) => ({
      snackbars: [
        ...state.snackbars,
        { id, message, severity, errorDetails, showContactSupport, duration },
      ],
    }));

    if (duration > 0) {
      setTimeout(() => {
        set((state) => ({
          snackbars: state.snackbars.filter((s) => s.id !== id),
        }));
      }, duration);
    }

    return id;
  },

  removeSnackbar: (id) => {
    set((state) => ({
      snackbars: state.snackbars.filter((s) => s.id !== id),
    }));
  },

  clearAll: () => {
    set({ snackbars: [] });
  },
}));

export interface UseSnackbarReturn {
  showSuccess: (message: string, duration?: number) => string;
  showError: (
    message: string,
    errorDetails?: string,
    showContactSupport?: boolean,
    duration?: number
  ) => string;
  showWarning: (message: string, duration?: number) => string;
  showInfo: (message: string, duration?: number) => string;
  addSnackbar: (
    message: string,
    severity: SnackbarSeverity,
    errorDetails?: string,
    showContactSupport?: boolean,
    duration?: number
  ) => string;
  removeSnackbar: (id: string) => void;
  clearAll: () => void;
}

/**
 * Hook for displaying snackbar notifications
 * Provides convenience methods for different severity levels
 */
export function useSnackbar(): UseSnackbarReturn {
  const addSnackbar = useSnackbarStore((state) => state.addSnackbar);
  const removeSnackbar = useSnackbarStore((state) => state.removeSnackbar);
  const clearAll = useSnackbarStore((state) => state.clearAll);

  return {
    showSuccess: (message: string, duration?: number) => {
      return addSnackbar(message, 'success', undefined, false, duration);
    },
    showError: (
      message: string,
      errorDetails?: string,
      showContactSupport?: boolean,
      duration?: number
    ) => {
      return addSnackbar(message, 'error', errorDetails, showContactSupport, duration);
    },
    showWarning: (message: string, duration?: number) => {
      return addSnackbar(message, 'warning', undefined, false, duration);
    },
    showInfo: (message: string, duration?: number) => {
      return addSnackbar(message, 'info', undefined, false, duration);
    },
    addSnackbar,
    removeSnackbar,
    clearAll,
  };
}

